import { useState } from 'react';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { GenericSuccess } from 'libs/end-to-end/src/generic';
import { EmptyObject } from 'libs/end-to-end/src/generic';
import { SharedAccessResourceDto } from 'libs/end-to-end/src/auth';
import { SharedAccessResourceType } from 'libs/end-to-end/src/auth';
import { useAsyncCallback } from 'apps/sigma2-ui/src/lib/hooks/useAsyncCallback';
import { apiCall } from 'apps/sigma2-ui/src/lib/api';
import { deserializeError } from 'libs/end-to-end/src/api-error';

export type SharedAccessState =
  | { state: 'idle' }
  | { state: 'checking-access' }
  | ({
      state: 'access-checked';
    } & SharedAccessResourceDto)
  | { state: 'access-check-failed'; errorMessage: string }
  | { state: 'requesting-code' }
  | { state: 'code-sent' }
  | { state: 'verifying' }
  | { state: 'request-failed'; errorMessage: string }
  | { state: 'verify-failed'; errorMessage: string }
  | { state: 'verified' };

type Params = {
  email: string;
  resourceId: string;
  resourceType: SharedAccessResourceType;
};

export const useSharedAccessAuth = () => {
  const [status, setStatus] = useState<SharedAccessState>({
    state: 'idle',
  });

  const checkAccess = useAsyncCallback(
    async (
      isAlive,
      params: { resourceId: string; resourceType: SharedAccessResourceType },
    ) => {
      if (!isAlive()) return;

      setStatus({ state: 'checking-access' });

      const response = await apiCall({
        url: `${getApiBaseUrl()}/auth/shared/resources/${params.resourceType}/${params.resourceId}/access`,
        deserializeErrorFn: deserializeError,
        requestOptions: {
          method: 'GET',
          credentials: 'include',
          cache: 'no-store',
        },
        responseSchema: SharedAccessResourceDto,
      });

      if (!isAlive()) return;

      if (!response || response.resultType === 'err') {
        setStatus({
          state: 'access-check-failed',
          errorMessage: 'Failed to check access',
        });
        return;
      }

      setStatus({
        state: 'access-checked',
        hasAccess: response.value.hasAccess,
        accessType: response.value.accessType,
      });
    },
    [],
  );

  const requestCode = useAsyncCallback(async (isAlive, params: Params) => {
    if (!isAlive()) return;

    setStatus({ state: 'requesting-code' });

    const response = await apiCall({
      url: `${getApiBaseUrl()}/auth/shared/request-code`,
      deserializeErrorFn: deserializeError,
      requestOptions: {
        method: 'POST',
        body: JSON.stringify(params),
      },
      responseSchema: GenericSuccess,
    });

    if (!isAlive()) return;

    if (!response || response.resultType === 'err' || !response.value.success) {
      setStatus({
        state: 'request-failed',
        errorMessage: 'Failed to send access code',
      });
      return;
    }

    setStatus({ state: 'code-sent' });
  }, []);

  const verifyCode = useAsyncCallback(
    async (isAlive, params: Params & { code: string }) => {
      if (!isAlive()) return;

      setStatus({ state: 'verifying' });

      const response = await apiCall({
        url: `${getApiBaseUrl()}/auth/shared/verify-code`,
        deserializeErrorFn: deserializeError,
        requestOptions: {
          method: 'POST',
          body: JSON.stringify(params),
          credentials: 'include',
        },
        responseSchema: EmptyObject,
      });

      if (!isAlive()) return;

      if (!response || response.resultType === 'err') {
        setStatus({
          state: 'verify-failed',
          errorMessage: 'Invalid or expired code',
        });
        return;
      }

      setStatus({ state: 'verified' });
    },
    [],
  );

  return {
    status,
    requestCode,
    verifyCode,
    checkAccess,
  };
};
